import * as AudioConstants from '../constants';

/**
 * @file Monitors for sufficient network bandwidth by measuring audio playhead position relative to the
 * amount of audio actually loaded, also taking into account any pauses in audio caused by buffering.
 *
 * The rules for detecting "low bandwidth" conditions are as follows:
 *
 * 	1) every time update look at the difference between the playhead percentage compared to the percentage actually loaded
 * 	2) if they are within 13seconds (about 5% of average length songs) of each other, and the difference is less than the last time it was checked, increment a counter
 * 	3) OR - if the difference is less than 2seconds (about 0.5% of average length songs), increment the counter
 * 	4) OR - if there has been a pause in audio caused by buffering, increment the counter
 * 	5) otherwise, clear the counter
 *
 * Basically we are trying to detect when there is not enough bandwidth to keep up with the bitrate of the audio -- the
 * playhead is "catching up" to the seeked (loaded) position of the file. Note that even if the playhead is within 5% of the seek position, if the "gap" between the two
 * is greater than last time, it indicates that the bandwidth may actually be sufficient, so we don't trigger a low
 * bandwidth condition. A good heuristic for consumers of this method might be that a warningCount of ../../10 would indicate conditions unsuitable for playback.
 */

/**
 * Check for low bandwith conditions
 *
 * @param {number} playedTime the playhead position, in seconds
 * @param {number} loadedPercent the loaded percentage as a percentage of total track length
 * @param {number} secondsOfAudioLeft the difference between the loaded seconds and playhead position
 * @param {number} lastDifference the last difference between loaded seconds and playhead position
 * @param {number} warningCount the count of warnings reported by this check function
 * @returns {object} and object containing the new difference and warning count
 */
export function check(playedTime, loadedPercent, secondsOfAudioLeft, lastDifference, warningCount) {
  let newDifference = lastDifference;
  let newWarningCount = warningCount;
  if (playedTime > 0 && loadedPercent < 99) {
    const veryLowDifference = secondsOfAudioLeft < 2;
    const wasLowPreviously = lastDifference < 2;
    const probablyStalled = wasLowPreviously && veryLowDifference;
    if (probablyStalled) {
      newWarningCount = Math.max(AudioConstants.MAX_BANDWIDTH_WARNINGS - 2, newWarningCount + 1);
    } else if (veryLowDifference) {
      newWarningCount++;
    } else {
      newWarningCount = 0;
    }
    newDifference = secondsOfAudioLeft;
  } else if (loadedPercent >= 99) {
    newWarningCount = 0;
  }
  return {
    lastDifference: newDifference,
    warningCount: newWarningCount,
  };
}
